/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import {
  useTable,
  appTableFeatures,
  flexRender,
  type ColumnDef,
  type Column,
  type RowData,
} from 'src/lib/tanstack-table';
import { Table, TableBody, TableRow } from 'src/components/ui/table';
import MDBox from 'src/components/md/MDBox';
import DataTableHeadCell from './DataTableHeadCell';
import DataTableBodyCell from './DataTableBodyCell';

/** Per-column layout hints, set via `meta` on a column definition. */
export interface DataTableColumnMeta {
  width?: string | number;
  align?: 'left' | 'right' | 'center';
}

function getColumnMeta<T extends RowData>(
  column: Column<T>,
): DataTableColumnMeta {
  return (column.columnDef.meta as DataTableColumnMeta | undefined) ?? {};
}

const DEFAULT_COLUMN_WIDTH = 150;

interface Props<T extends RowData> {
  table: {
    columns: ColumnDef<T>[];
    rows: T[];
  };
}

/** Static, unsorted, unpaginated table in the legacy Material Dashboard style. */
function DataTable<T extends RowData>({ table }: Props<T>) {
  const tableInstance = useTable({
    features: appTableFeatures,
    columns: table.columns,
    data: table.rows,
  });

  return (
    <div className="overflow-x-auto">
      <Table>
        <MDBox component="thead">
          {tableInstance.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                // react-table v7 gave every column a 150px default width.
                const { width = DEFAULT_COLUMN_WIDTH, align } = getColumnMeta(
                  header.column,
                );
                return (
                  <DataTableHeadCell
                    key={header.id}
                    width={width}
                    align={align}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </DataTableHeadCell>
                );
              })}
            </TableRow>
          ))}
        </MDBox>
        <TableBody>
          {tableInstance.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getAllCells().map((cell) => (
                <DataTableBodyCell
                  key={cell.id}
                  align={getColumnMeta(cell.column).align}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </DataTableBodyCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default DataTable;
